import React, { useState, useEffect, useMemo } from 'react'
import { Form, Button, Card, Alert, Container, Row, Col, Badge, Spinner } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { supabase } from '../services/supabase'

// ====== TEXTES STATIQUES ======
const TEXT_INTRO =
  `Ce document comprend des explications sur le projet mais également un questionnaire afin de faire plus ample connaissance et déterminer vos besoins.

Toutes les données reprises ci-dessous seront traitées avec la plus grande confidentialité.`

const TEXT_INFO_PROJET =
`Informations sur le projet Streetsport

Le concept du projet
L'objectif du projet Streetsport est de créer des conditions favorables permettant aux adolescents, aux jeunes adultes :

· De gérer d'éventuelles difficultés psychologiques ou sociales ;
· De permettre, si nécessaire, le rétablissement de liens familiaux ou sociaux ;
· D'établir la communication entre les différents groupes de jeunes dans un souci d'intégration à la norme ;
· D'établir ou renforcer les contacts entre les communautés et favoriser la reconnaissance de l'égalité des sexes ;
· De favoriser la mixité intergénérationnelle au travers des entraînements sportifs ouverts à tous et aussi grâce aux contacts et démonstrations dans les quartiers ;
· D'ouvrir les adolescents à différentes cultures, de les initier à des pratiques d'échanges communautaires et de coopérations inter associatives. En effet, les sports de combat, tout comme la danse ont des origines divers : asiatique, sud-américaine, maghrébine ou sont issus des ghettos.

L'école des sports est un pôle fédérateur où les différentes philosophies se côtoient. Ceci encourage les jeunes à aller à la découverte de cultures et de peuples différents, ainsi que de se faire une idée personnelle de la différence et de la vivre.

Ce projet sportif est encadré et coordonné par Mme. Tamara Vujovic, auprès de laquelle vous pouvez, en cas de besoin, vous adresser au numéro suivant : 621 216 835 ou par e-mail : streetsport@i-a.lu. Les entraîneurs servent aussi de relais entre le participant et les éducateurs entourant ce projet.`

const TEXT_REGLEMENT =
`Le règlement du projet Streetsport

1. Chaque participant respectera les autres.
2. Un comportement civilisé et fair-play est impératif.
3. Tout acte de violence entraînera l'exclusion du projet Streetsport.
4. Chaque participant entretiendra et respectera le matériel appartenant au projet Streetsport.
5. Les utilisateurs veilleront à tenir la salle d'entraînement et ses alentours dans un état de propreté.
6. La consommation et la détention de drogues ou d'alcool sont strictement défendues dans et aux alentours des salles du Streetsport.
7. Les participants sont priés de respecter les horaires d'entraînement.
8. Les participants s'engagent à ne pas utiliser les techniques apprises au sein du projet Streetsport sous aucun prétexte (agression, jeux, démonstration,…).

Les points du règlement du Streetsport sont à respecter par tous les participants ou visiteurs. Ces différents points peuvent être changés à tout moment, si nécessaire.

Tout manquement au respect du règlement (conduite violente envers autrui, dégradation de matériel,…) entraîne des conséquences immédiates pouvant aller jusqu'à l'exclusion du projet pour une période déterminée.`

const TEXT_DECLARATION =
`Déclaration d'inscription

Valable pour l'année scolaire
(l'année scolaire couvre la période du 15.9. au 14.9. de l'année suivante)

Si vous êtes majeur il faut seulement remplir l'espace qui suit.

Par la présente, je soussigné(e) m'inscris pour l'atelier indiqué en première page. Je déclare que j'ai pris connaissance de toutes les informations relevées dans ce document (de la page 1 à la page 6) et que les informations fournies sur ma personne sont exactes.

Signature (avec la mention "lu et approuvé").`

const TEXT_AUTORISATION =
`Autorisation parentale

Valable pour l'année scolaire
(l'année scolaire couvre la période du 15.9. au 14.9. de l'année suivante)

Par la présente, je soussigné(e) autorise mon enfant à participer aux ateliers de sport, excursions et activités organisées par Inter-Actions dans le cadre de son projet Streetsport. J'ai bien pris note :
- que les ateliers de sport/danse comprennent aussi des disciplines d'arts martiaux, de sports de combat ou danse présentant un risque d'accident,
- que les ateliers offerts sont entre-autres Boxe-Thai, Capoeira, Mix Martial Arts (M.M.A.), Boxe-Fit pour Femmes, Grappling.
- que durant l'année scolaire en cours des ateliers peuvent s'ajouter à la liste précédente sans que ceci affecte la présente autorisation,
- que des excursions et activités peuvent se réaliser à l'étranger,
- que des renseignements supplémentaires peuvent être pris par téléphone (691 869 321) auprès du coordinateur du Streetsport,
- qu'une copie de la présente autorisation me sera retournée par courrier afin de vérifier d'éventuelles inexactitudes,
- que des photos et enregistrement vidéos prises pendant les différents cours et démonstrations peuvent être utilisé dans l'intérêt du projet (site internet Streetsport - Inter-Actions, flyers, brochures, etc…)
- que la présente autorisation est valable pendant une année scolaire et sera renouvelée la prochaine année scolaire si votre enfant continue à participer et s'il n'a pas encore atteint sa majorité.

Signature (avec la mention "lu et approuvé")
(il suffit une personne).`

// ====== Helpers ======
function calcAge(birthDate) {
  if (!birthDate) return null
  
  // Gérer les différents formats de date
  let d
  if (typeof birthDate === 'string') {
    // Si c'est une string au format YYYY-MM-DD (format input date)
    if (birthDate.includes('-') && birthDate.length === 10) {
      const [year, month, day] = birthDate.split('-').map(Number)
      d = new Date(year, month - 1, day)
    } else {
      d = new Date(birthDate)
    }
  } else {
    d = new Date(birthDate)
  }
  
  if (Number.isNaN(d.getTime())) return null
  
  const today = new Date()
  let age = today.getFullYear() - d.getFullYear()
  const monthDiff = today.getMonth() - d.getMonth()
  
  // Ajuster si l'anniversaire n'est pas encore passé cette année
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) {
    age--
  }
  
  return age
}

// Layout simple
const SimpleLayout = ({ children }) => (
  <div className="min-vh-100 bg-light">
    <Container className="py-4">
      {children}
    </Container>
  </div>
)

export default function CompleteProfile() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [initialLoading, setInitialLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [participant, setParticipant] = useState(null)

  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    birthDate: '',
    sex: '',
    nationality: '',
    maritalStatus: '',
    address: '',
    addressNumber: '',
    postalCode: '',
    city: '',
    phoneHome: '',
    phoneMobile: '',
    email: '',

    disciplines: {
      boxe_thai: false,
      capoeira: false,
      mma: false,
      boxe_fit: false,
      grappling: false,
      autre: ''
    },

    q_family: '',
    q_studies: '',
    q_occupation: '',
    q_working: '',
    q_hobbies: '',
    q_youth_houses: '',
    q_leisure_sites: '',
    q_social_associations: '',

    q_health: '',
    q_fitness: '',
    q_treatment: '',
    q_surgery: '',
    q_allergies: '',

    p_known: '',
    p_after: '',
    p_practiced: '',
    p_opinion: '',
    p_why: '',
    p_change: '',
    p_expected: '',
    p_reason_absence: '',
    p_abroad: '',
    p_future: '',

    rgpdConsent: false,
    rulesAccepted: false,
    luEtApprouve: false,

    parentChoice: '',
    parentLastName: '',
    parentFirstName: '',
    parentAddress: '',
    parentLuEtApprouve: false,
  })

  const age = useMemo(() => calcAge(formData.birthDate), [formData.birthDate])
  const isMinor = age !== null ? age < 18 : false

  // Charger les données du participant
  useEffect(() => {
    if (!token) {
      setError("Token manquant dans l'URL")
      setInitialLoading(false)
      return
    }
    fetchParticipant()
  }, [token])

  const fetchParticipant = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('participants')
        .select('*')
        .eq('token', token)
        .single()

      if (fetchError) throw new Error("Participant introuvable avec ce token")

      setParticipant(data)
      
      // Pré-remplir TOUS les champs avec les données existantes
      setFormData({
        lastName: data.last_name || '',
        firstName: data.first_name || '',
        birthDate: data.birth_date || '',
        sex: data.sex || '',
        nationality: data.nationality || '',
        maritalStatus: data.marital_status || '',
        address: data.address || '',
        addressNumber: data.address_number || '',
        postalCode: data.postal_code || '',
        city: data.city || '',
        phoneHome: data.phone_home || '',
        phoneMobile: data.phone_mobile || '',
        email: data.email || '',

        disciplines: data.disciplines || {
          boxe_thai: false,
          capoeira: false,
          mma: false,
          boxe_fit: false,
          grappling: false,
          autre: ''
        },

        q_family: data.questionnaire?.generalites?.a_situation_familiale || '',
        q_studies: data.questionnaire?.generalites?.b_niveau_etude || '',
        q_occupation: data.questionnaire?.generalites?.c_occupation || '',
        q_working: data.questionnaire?.generalites?.d_travail || '',
        q_hobbies: data.questionnaire?.generalites?.e_hobbies || '',
        q_youth_houses: data.questionnaire?.generalites?.f_maisons_jeunes || '',
        q_leisure_sites: data.questionnaire?.generalites?.g_loisirs_site || '',
        q_social_associations: data.questionnaire?.generalites?.h_associations || '',

        q_health: data.questionnaire?.sante?.a_bonne_sante || '',
        q_fitness: data.questionnaire?.sante?.b_condition_physique || '',
        q_treatment: data.questionnaire?.sante?.c_traitement || '',
        q_surgery: data.questionnaire?.sante?.d_operations || '',
        q_allergies: data.questionnaire?.sante?.e_allergies || '',

        p_known: data.questionnaire?.projet?.a_comment_connu || '',
        p_after: data.questionnaire?.projet?.b_apres_lecture || '',
        p_practiced: data.questionnaire?.projet?.c_pratique_avant || '',
        p_opinion: data.questionnaire?.projet?.d_opinion || '',
        p_why: data.questionnaire?.projet?.e_pourquoi || '',
        p_change: data.questionnaire?.projet?.f_changement || '',
        p_expected: data.questionnaire?.projet?.g_resultat_attendu || '',
        p_reason_absence: data.questionnaire?.projet?.h_raison_absence || '',
        p_abroad: data.questionnaire?.projet?.i_stage_etranger || '',
        p_future: data.questionnaire?.projet?.j_futur || '',

        rgpdConsent: data.rgpd_consent || false,
        rulesAccepted: data.rules_accepted || false,
        luEtApprouve: data.lu_et_approuve || false,

        parentChoice: data.questionnaire?.autorisation_parentale?.parent || '',
        parentLastName: data.questionnaire?.autorisation_parentale?.nom || '',
        parentFirstName: data.questionnaire?.autorisation_parentale?.prenom || '',
        parentAddress: data.questionnaire?.autorisation_parentale?.adresse || '',
        parentLuEtApprouve: data.questionnaire?.autorisation_parentale?.lu_et_approuve || false,
      })

    } catch (err) {
      setError(err.message || "Erreur lors du chargement des données")
    } finally {
      setInitialLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleDisciplineChange = (e) => {
    const { name, type, checked, value } = e.target
    setFormData(prev => ({
      ...prev,
      disciplines: {
        ...prev.disciplines,
        [name]: type === 'checkbox' ? checked : value
      }
    }))
  }

  const validate = () => {
    if (!token) return "Token manquant."
    if (!formData.lastName || !formData.firstName) return "Nom et prénom sont obligatoires."
    if (!formData.birthDate) return "Date de naissance obligatoire."
    if (!formData.sex) return "Sexe obligatoire."
    if (!formData.rgpdConsent) return "Vous devez accepter le RGPD."
    if (!formData.rulesAccepted) return "Vous devez accepter le règlement."
    if (!formData.luEtApprouve) return `Vous devez cocher "Lu et approuvé".`

    if (isMinor) {
      if (!formData.parentChoice) return "Mineur : veuillez préciser père/mère/tuteur."
      if (!formData.parentLastName || !formData.parentFirstName) return "Mineur : nom/prénom parent requis."
      if (!formData.parentAddress) return "Mineur : adresse parent requise."
      if (!formData.parentLuEtApprouve) return `Mineur : le parent doit cocher "Lu et approuvé".`
    }
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const v = validate()
    if (v) {
      setError(v)
      setLoading(false)
      return
    }

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        birth_date: formData.birthDate || null,
        sex: formData.sex,
        nationality: formData.nationality || null,
        marital_status: formData.maritalStatus || null,

        address: formData.address || null,
        address_number: formData.addressNumber || null,
        postal_code: formData.postalCode || null,
        city: formData.city || null,

        phone_home: formData.phoneHome || null,
        phone_mobile: formData.phoneMobile || null,
        email: formData.email || null,

        disciplines: formData.disciplines,

        questionnaire: {
          generalites: {
            a_situation_familiale: formData.q_family,
            b_niveau_etude: formData.q_studies,
            c_occupation: formData.q_occupation,
            d_travail: formData.q_working,
            e_hobbies: formData.q_hobbies,
            f_maisons_jeunes: formData.q_youth_houses,
            g_loisirs_site: formData.q_leisure_sites,
            h_associations: formData.q_social_associations
          },
          sante: {
            a_bonne_sante: formData.q_health,
            b_condition_physique: formData.q_fitness,
            c_traitement: formData.q_treatment,
            d_operations: formData.q_surgery,
            e_allergies: formData.q_allergies
          },
          projet: {
            a_comment_connu: formData.p_known,
            b_apres_lecture: formData.p_after,
            c_pratique_avant: formData.p_practiced,
            d_opinion: formData.p_opinion,
            e_pourquoi: formData.p_why,
            f_changement: formData.p_change,
            g_resultat_attendu: formData.p_expected,
            h_raison_absence: formData.p_reason_absence,
            i_stage_etranger: formData.p_abroad,
            j_futur: formData.p_future
          },
          autorisation_parentale: isMinor ? {
            parent: formData.parentChoice,
            nom: formData.parentLastName,
            prenom: formData.parentFirstName,
            adresse: formData.parentAddress,
            lu_et_approuve: formData.parentLuEtApprouve
          } : null
        },

        rgpd_consent: formData.rgpdConsent,
        rules_accepted: formData.rulesAccepted,
        lu_et_approuve: formData.luEtApprouve,
        updated_at: new Date().toISOString()
      }

      const { error: updateError } = await supabase
        .from('participants')
        .update(payload)
        .eq('token', token)

      if (updateError) throw new Error(updateError.message)

      setParticipant({ ...participant, ...payload })
      setSuccess(true)

    } catch (err) {
      setError(err.message || "Une erreur est survenue lors de la mise à jour")
    }

    setLoading(false)
  }

  // État de chargement initial
  if (initialLoading) {
    return (
      <SimpleLayout>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow-sm">
              <Card.Body className="text-center py-5">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <p className="text-muted">Chargement de votre profil...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </SimpleLayout>
    )
  }

  // Token invalide
  if (!token || error.includes("introuvable")) {
    return (
      <SimpleLayout>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow-sm">
              <Card.Body className="text-center py-5">
                <div style={{ fontSize: '4rem' }} className="text-danger mb-3">❌</div>
                <h4>Lien invalide</h4>
                <p className="text-muted">{error || "Le lien de complétion de profil est invalide ou expiré."}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </SimpleLayout>
    )
  }

  // Succès
  if (success && participant) {
    return (
      <SimpleLayout>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow-sm">
              <Card.Body className="text-center">
                <div style={{ fontSize: '4rem' }} className="text-success mb-3">✅</div>
                <h3 className="text-success">Profil mis à jour !</h3>
                <p className="lead mb-3">
                  Merci <strong>{participant.first_name} {participant.last_name}</strong>
                </p>

                <div className="my-4 p-4 bg-light rounded">
                  <p className="mb-3"><strong>Votre QR code personnel :</strong></p>
                  <QRCodeSVG value={participant.token} size={200} includeMargin />
                  <p className="text-muted small mt-3">
                    Conservez ce QR code pour marquer votre présence à chaque session.
                  </p>
                </div>

                <Button variant="primary" size="lg" onClick={() => window.print()}>
                  🖨️ Imprimer le QR Code
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </SimpleLayout>
    )
  }

  // Formulaire (exactement comme Register)
  return (
    <SimpleLayout>
      <Row className="justify-content-center">
        <Col md={10} lg={8}>

          <div className="text-center mb-3">
            <h1 className="fw-bold text-primary mb-1">Street Sport Luxembourg</h1>
            <h4 className="mb-0">Compléter votre fiche d'inscription</h4>
          </div>

          <Card className="shadow-sm mb-4">
            <Card.Body style={{ whiteSpace: 'pre-wrap' }}>
              {TEXT_INTRO}
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Informations personnelles</h5>
                {age !== null && (
                  <Badge bg={isMinor ? 'warning' : 'success'}>
                    {isMinor ? `Mineur (${age} ans)` : `Majeur (${age} ans)`}
                  </Badge>
                )}
              </div>

              {error && (
                <Alert variant="danger" className="mb-3">
                  <strong>Erreur :</strong> {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Nom *</Form.Label>
                      <Form.Control name="lastName" value={formData.lastName} onChange={handleChange} required size="lg" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Prénom *</Form.Label>
                      <Form.Control name="firstName" value={formData.firstName} onChange={handleChange} required size="lg" />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Date de naissance *</Form.Label>
                      <Form.Control type="date" name="birthDate" value={formData.birthDate} onChange={handleChange} required size="lg" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Sexe *</Form.Label>
                      <Form.Select name="sex" value={formData.sex} onChange={handleChange} required size="lg">
                        <option value="">Choisir…</option>
                        <option value="Masculin">Masculin</option>
                        <option value="Féminin">Féminin</option>
                        <option value="Autre">Autre</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Nationalité</Form.Label>
                      <Form.Control name="nationality" value={formData.nationality} onChange={handleChange} size="lg" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>État civil</Form.Label>
                      <Form.Control name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} size="lg" />
                    </Form.Group>
                  </Col>

                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Adresse</Form.Label>
                      <Form.Control name="address" value={formData.address} onChange={handleChange} size="lg" />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Nr</Form.Label>
                      <Form.Control name="addressNumber" value={formData.addressNumber} onChange={handleChange} size="lg" />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Code postal</Form.Label>
                      <Form.Control name="postalCode" value={formData.postalCode} onChange={handleChange} size="lg" />
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Localité</Form.Label>
                      <Form.Control name="city" value={formData.city} onChange={handleChange} size="lg" />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Tel Privé</Form.Label>
                      <Form.Control name="phoneHome" value={formData.phoneHome} onChange={handleChange} size="lg" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Tel portable</Form.Label>
                      <Form.Control name="phoneMobile" value={formData.phoneMobile} onChange={handleChange} size="lg" />
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Adresse e-mail</Form.Label>
                      <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} size="lg" />
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" />
                <h5>La ou les disciplines pratiquées au sein du projet Streetsport</h5>

                <Card className="mb-3">
                  <Card.Body>
                    <Form.Check type="checkbox" name="boxe_thai" checked={formData.disciplines.boxe_thai} onChange={handleDisciplineChange} label="Boxe-Thai" />
                    <Form.Check type="checkbox" name="capoeira" checked={formData.disciplines.capoeira} onChange={handleDisciplineChange} label="Capoeira" />
                    <Form.Check type="checkbox" name="mma" checked={formData.disciplines.mma} onChange={handleDisciplineChange} label="Mix Martial Arts (M.M.A.)" />
                    <Form.Check type="checkbox" name="boxe_fit" checked={formData.disciplines.boxe_fit} onChange={handleDisciplineChange} label="Boxe-Fit pour Femmes" />
                    <Form.Check type="checkbox" name="grappling" checked={formData.disciplines.grappling} onChange={handleDisciplineChange} label="Grappling" />
                    <Form.Group className="mt-2">
                      <Form.Label>Autre</Form.Label>
                      <Form.Control name="autre" value={formData.disciplines.autre} onChange={handleDisciplineChange} />
                    </Form.Group>
                  </Card.Body>
                </Card>

                <hr className="my-4" />
                <Card className="mb-3">
                  <Card.Header className="fw-bold">Informations sur le projet Streetsport</Card.Header>
                  <Card.Body style={{ whiteSpace: 'pre-wrap' }}>{TEXT_INFO_PROJET}</Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header className="fw-bold">Le règlement du projet Streetsport</Card.Header>
                  <Card.Body style={{ whiteSpace: 'pre-wrap' }}>{TEXT_REGLEMENT}</Card.Body>
                </Card>

                <hr className="my-4" />
                <h4 className="mb-3">Questionnaire</h4>

                <h5>1. GENERALITES</h5>
                <Form.Group className="mb-3">
                  <Form.Label>a) Situation en milieu familial :</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_family" value={formData.q_family} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>b) Niveau et lieu d'étude (dernier diplôme obtenu) :</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_studies" value={formData.q_studies} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>c) Occupation actuelle ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_occupation" value={formData.q_occupation} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>d) Travaillez-vous actuellement ? Oui/Non. Si oui, où exactement ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_working" value={formData.q_working} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>e) Citez quatre centres d'intérêt (hobbies) :</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_hobbies" value={formData.q_hobbies} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>f) Connaissez-vous une ou plusieurs maisons de jeunes à Luxembourg Ville ? Si oui lesquelles ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_youth_houses" value={formData.q_youth_houses} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>g) Connaissez-vous un organisme ou un site web d'information sur les loisirs au Luxembourg ? Si oui lequel ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_leisure_sites" value={formData.q_leisure_sites} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>h) Connaissez-vous des associations de services sociaux et/ou culturels au Luxembourg ? Si oui lesquelles ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_social_associations" value={formData.q_social_associations} onChange={handleChange} />
                </Form.Group>

                <h5>2. SANTE / CONDITION PHYSIQUE</h5>
                <Form.Group className="mb-3">
                  <Form.Label>a) Êtes-vous en bonne santé ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_health" value={formData.q_health} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>b) Êtes-vous en bonne condition physique ? Faites-vous du sport régulièrement ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_fitness" value={formData.q_fitness} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>c) Suivez-vous un traitement médical ? Prenez-vous des médicaments ? Si oui lesquels ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_treatment" value={formData.q_treatment} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>d) Opérations récemment ? Si oui lesquelles ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_surgery" value={formData.q_surgery} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>e) Allergies ou autres maladies ? Si oui lesquelles ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="q_allergies" value={formData.q_allergies} onChange={handleChange} />
                </Form.Group>

                <h5>3. LE PROJET STREETSPORT</h5>
                <Form.Group className="mb-3">
                  <Form.Label>a) Comment avez-vous connu le projet ? Que savez-vous concrètement ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_known" value={formData.p_known} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>b) Que pensez-vous du projet après lecture du concept (page 1) ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_after" value={formData.p_after} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>c) Avez-vous déjà pratiqué un sport/danse ? Si oui lesquels ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_practiced" value={formData.p_practiced} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>d) Que pensez-vous des arts martiaux, sports de combat et danse ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_opinion" value={formData.p_opinion} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>e) Pourquoi voulez-vous pratiquer ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_why" value={formData.p_why} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>f) Pensez-vous que cela vous apportera un changement ? Expliquez.</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_change" value={formData.p_change} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>g) Quel résultat attendez-vous ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_expected" value={formData.p_expected} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>h) Raison impérative pour ne pas participer ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_reason_absence" value={formData.p_reason_absence} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>i) Stage sportif/danse à l'étranger ? Expliquez.</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_abroad" value={formData.p_abroad} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>j) Comment vous imaginez-vous dans le futur ?</Form.Label>
                  <Form.Control as="textarea" rows={2} name="p_future" value={formData.p_future} onChange={handleChange} />
                </Form.Group>

                <hr className="my-4" />
                <Card className="mb-3">
                  <Card.Header className="fw-bold">Déclaration d'inscription</Card.Header>
                  <Card.Body style={{ whiteSpace: 'pre-wrap' }}>{TEXT_DECLARATION}</Card.Body>
                </Card>

                <Form.Check
                  className="mb-2"
                  type="checkbox"
                  name="rgpdConsent"
                  checked={formData.rgpdConsent}
                  onChange={handleChange}
                  label="J'accepte le traitement de mes données personnelles conformément au RGPD."
                  required
                />
                <Form.Check
                  className="mb-2"
                  type="checkbox"
                  name="rulesAccepted"
                  checked={formData.rulesAccepted}
                  onChange={handleChange}
                  label="J'ai pris connaissance du règlement Streetsport et je m'engage à le respecter."
                  required
                />
                <Form.Check
                  className="mb-4"
                  type="checkbox"
                  name="luEtApprouve"
                  checked={formData.luEtApprouve}
                  onChange={handleChange}
                  label='Signature : "Lu et approuvé" (cocher oui).'
                  required
                />

                {isMinor && (
                  <>
                    <hr className="my-4" />
                    <Card className="mb-3 border-warning">
                      <Card.Header className="fw-bold">Autorisation parentale (obligatoire si mineur)</Card.Header>
                      <Card.Body style={{ whiteSpace: 'pre-wrap' }}>
                        {TEXT_AUTORISATION}
                      </Card.Body>
                    </Card>

                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Père / Mère / Tuteur *</Form.Label>
                          <Form.Select name="parentChoice" value={formData.parentChoice} onChange={handleChange} required size="lg">
                            <option value="">Choisir…</option>
                            <option value="pere">Père</option>
                            <option value="mere">Mère</option>
                            <option value="tuteur">Tuteur</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Nom (parent) *</Form.Label>
                          <Form.Control name="parentLastName" value={formData.parentLastName} onChange={handleChange} required size="lg" />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Prénom (parent) *</Form.Label>
                          <Form.Control name="parentFirstName" value={formData.parentFirstName} onChange={handleChange} required size="lg" />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Adresse (parent) *</Form.Label>
                          <Form.Control as="textarea" rows={2} name="parentAddress" value={formData.parentAddress} onChange={handleChange} required />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Check
                      className="mb-4"
                      type="checkbox"
                      name="parentLuEtApprouve"
                      checked={formData.parentLuEtApprouve}
                      onChange={handleChange}
                      label='Signature parent : "Lu et approuvé" (cocher oui).'
                      required
                    />
                  </>
                )}

                <Button
                  variant="success"
                  type="submit"
                  className="w-100 py-3"
                  disabled={loading}
                  size="lg"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Mise à jour en cours...
                    </>
                  ) : (
                    "✅ Mettre à jour mon profil"
                  )}
                </Button>

              </Form>
            </Card.Body>
          </Card>

        </Col>
      </Row>
    </SimpleLayout>
  )
}